import { cmp, factor, isNegative, money } from '@fameacc/money';
import { z } from 'zod';
import { bankTransactionSchema } from './bank.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Verträge der Kontierungsablage — Aufgabe 8 `[C-kontapi]` (Spec
 * `2026-08-26-sachkonten-abgleich-design.md`, Abschnitt 3 und 5.4).
 *
 * **Kein Geldbetrag als `number`, auch nicht hier.** `centAmount` gibt ein
 * `Money` zurück, nie eine Zeichenkette — Hausregel 1 gilt an der Aussengrenze
 * genauso wie im Dienst.
 *
 * **Kopf und Positionen, seit der Kanzleiantwort auf `KS-4` (2026-08-26).**
 * Migration `0074_kontierung.sql` trägt die vollständige Begründung; hier
 * steht nur die Form, in der beides über die Leitung geht.
 */
/**
 * Wie eine Kontierung ALS GANZES entstanden ist — die Messgrösse der Ära
 * (Spec 3.3). Deckungsgleich mit dem `CHECK kontierung_herkunft_bekannt` aus
 * Migration `0074`.
 */
/**
 * `aufgehoben` und `storniert` — Nachtrag Auftrag `W-kontkorrektur`
 * (2026-08-27), Migration `0082`. Beide behaupten das GEGENTEIL einer
 * Sachkontenentscheidung — "diese Bewegung trägt aktuell keine gültige
 * Kontierung" — und tragen deshalb keine Position (Kopfkommentar der
 * Migration). `aufgehoben`: ein Mensch löst eine noch nicht gebuchte
 * Kontierung ohne Ersatz (Stufe A). `storniert`: der Terminalzustand NACH
 * einer Journal-Stornobuchung (Stufe B, `KontierungService.stornieren()`).
 */
/**
 * `automatisch` — Nachtrag Auftrag `R-aufladungsauto` (2026-08-28), Migration
 * `0092`: die **erste Regel der Regel-Engine-Ära**. Eine erkannte
 * Guthabenaufladung wird ohne Menschenklick kontiert, freigegeben und gebucht
 * (Entscheidung des Auftraggebers vom 2026-08-28, Ask-Runde, Option
 * „Vollautomatisch"). Die Zeile behauptet eine Sachkontenentscheidung wie
 * `von_hand` — genau eine Position auf `1796` über den Bewegungsbetrag —, nur
 * hat sie kein Mensch getroffen.
 *
 * **Sie ist über die Anlage NICHT behauptbar.** `KontierungService.anlegen()`
 * weist `herkunft = 'automatisch'` mit `KONTIERUNG_HERKUNFT_AUTOMATISCH` ab;
 * der Wert entsteht ausschliesslich auf dem Regelweg
 * (`KontierungService.automatischKontieren()`). Er steht trotzdem in DIESEM
 * Schema und nicht in einem zweiten, nur lesenden: Die Aufzählung ist
 * deckungsgleich mit dem `CHECK kontierung_herkunft_bekannt` gehalten, und
 * eine zweite Aufzählung wäre genau die Drift, gegen die
 * `scripts/verify-api-enums.mjs` gebaut ist. Die Grenze steht am Dienst, wo
 * sie hingehört und wo `E-503` sie prüft.
 */
/**
 * `altbestand` — Nachtrag Auftrag `R-altbestand` (2026-08-28), Migration
 * `0096`: der einmalige Nachlauf über den Rückstand VOR der
 * Aktivierungsgrenze der laufenden Regel. Die Zeile behauptet dieselbe
 * Sachkontenentscheidung wie `automatisch` — genau eine Position auf `1796`
 * über den Bewegungsbetrag —, aber sie kommt nicht aus einer dauerhaft
 * laufenden Regel, sondern aus einem ausdrücklich beauftragten, trocken
 * vorgeführten Lauf.
 *
 * **Getrennt geführt und nicht unter `automatisch` mitgeführt**, damit die
 * Nachschau über die laufende Regel (rund 50 Zeilen im Monat) nicht in einer
 * einmaligen Nachbuchung untergeht. Vollständige Begründung im Kopf von
 * `0096_altbestand_nachbuchung.sql`, Entscheidung 1.
 *
 * **Auch dieser Wert ist über die Anlage NICHT behauptbar**
 * (`KONTIERUNG_HERKUNFT_ALTBESTAND`, `E-509`) — aus demselben Grund wie bei
 * `automatisch`: Für beide gilt `kontierung_vier_augen` nicht.
 */
/**
 * `zd_auszahlung`, `zd_gebuehr`, `pp_lastschrift` — Nachtrag Auftrag
 * `R-regeln234` (2026-08-29), Migrationen `0098`–`0100`: die drei weiteren
 * maschinellen Marken, je eine Regel des Regelregisters.
 *
 * **Auch sie sind über die Anlage NICHT behauptbar**
 * (`KONTIERUNG_HERKUNFT_REGEL`) — aus demselben Grund wie bei `automatisch`
 * und `altbestand`: Für alle fünf gilt `kontierung_vier_augen` nicht, und ein
 * behauptbarer Wert machte aus der Ausnahme einen Schalter.
 *
 * **Sie stehen hier, weil die LESESEITE sie braucht.** `kontierung.herkunft`
 * trägt diese Werte in der Datenbank; ein Vertrag, der sie nicht kennt,
 * behauptete beim Lesen einen Typ, den die Zeile nicht hat — und die Oberfläche
 * bekäme einen Wert, für den sie keinen Zweig hat, ohne dass irgendetwas laut
 * würde.
 */
/**
 * `zd_auszahlung_alt`, `pp_lastschrift_alt` — Nachtrag Auftrag
 * `R-altbestand2` (2026-08-29), Migration `0102`: der einmalige Nachlauf über
 * den Rückstand der Regeln 2 und 4 vor deren Aktivierungsgrenze (`REGELN234_AB`,
 * `apps/api/src/regeln/spuren.ts`) — dieselbe Zweiteilung wie `altbestand`
 * gegen `automatisch`, nur für zwei Regeln statt einer und deshalb mit zwei
 * eigenen Marken statt einer gemeinsamen.
 *
 * **Bis Befund 3 aus `R-azrueckbuchung` (2026-09-03, `I-herkunftschema`)
 * fehlten beide hier**, obwohl Migration `0102` sie in `kontierung_herkunft_bekannt`
 * und `kontierung_vier_augen` bereits seit dem 2026-08-29 führte: Die
 * LESESEITE wies eine richtige Zeile ab. Der Test, der das jetzt festhält,
 * hält den Vertrag direkt gegen `pg_get_constraintdef` von
 * `kontierung_herkunft_bekannt` — die Sollliste ist die Datenbank, keine
 * zweite Abschrift (`apps/api/test/kontierung/herkunft-vertrag.test.ts`).
 *
 * **Auch diese zwei Werte sind über die Anlage NICHT behauptbar**, aus
 * demselben Grund wie ihre laufenden Geschwister.
 */
/**
 * `datenregel_automatisch` — Nachtrag Auftrag `R-drautomatik` (2026-09-01),
 * Migration `0113`: die **eine** Marke aller vollautomatischen Datenregeln
 * (`D-1`).
 *
 * **Eine und nicht eine je Regel.** Welche Regel gebucht hat, sagt der
 * Fremdschlüssel `kontierung.regel_version_id` — die Regelmenge steht in der
 * Datenbank und nicht im Quelltext, und eine Marke je Regel wäre eine
 * Migration je angelegter Regel. Marke und Spalte sind per CHECK aneinander
 * gebunden (`E-593`).
 *
 * **Auch dieser Wert ist über die Anlage NICHT behauptbar**
 * (`KONTIERUNG_HERKUNFT_REGEL`) — aus demselben Grund wie bei den sieben
 * Code-Marken: Für ihn gilt `kontierung_vier_augen` nicht. Er steht hier, weil
 * die LESESEITE ihn braucht.
 */
export const kontierungHerkunftSchema = z.enum([
    'vorschlag_angenommen',
    'vorschlag_geaendert',
    'von_hand',
    'korrektur',
    'aufgehoben',
    'storniert',
    'automatisch',
    'altbestand',
    'zd_auszahlung',
    'zd_gebuehr',
    'pp_lastschrift',
    // `R-altbestand2` (2026-08-29), Migration `0102`: der einmalige Nachlauf
    // der Regeln 2 und 4 -- nachgetragen erst durch `R-azrueckbuchung`s
    // Befund 3 (`I-herkunftschema`, 2026-09-03), siehe Kommentar oben.
    'zd_auszahlung_alt',
    'pp_lastschrift_alt',
    'datenregel_automatisch',
    // `R-azrueckbuchung` (2026-09-03), Migration `0141`: die zwei Marken der
    // Regel 5 -- die laufende Spur und ihr einmaliger Nachlauf.
    'az_rueckbuchung',
    'az_rueckbuchung_alt',
    // `GU-fintsaufladung` (2026-09-05), Migration `0164`: der dritte Lauf ueber
    // den Bankkanal-Rueckstand der Guthabenaufladungen (E-881/E-882).
    'bankaufladung',
    // `ZZ-schema` (2026-09-06), Migration `0173`: die Kontierung, die aus der
    // Freigabe einer Zahlungszuordnung entsteht (`D-13`). Anders als die
    // maschinellen Marken darueber ist sie NICHT gleichbedeutend mit "ohne
    // Mensch" -- sie steht auch an einer von Hand freigegebenen Zuordnung; nur
    // der reine Volltreffer bucht ohne Vier-Augen (`D-14`), und dafuer traegt
    // `kontierung_vier_augen` sie in seiner Ausnahmeliste.
    'zahlungszuordnung',
    // `K-auszahlungsbuchung` (2026-09-08), Migration `0196`: der Handlauf ueber
    // die MOLLIE-Auszahlungen, die Regel 2 mit dem Hindernis
    // `dienst_nicht_erfasst` abweist (`O-11`). Dritte Marke der Regel 2, wie
    // `bankaufladung` die dritte der Regel 1 ist -- E-1051.
    'zd_auszahlung_mollie',
]);
/**
 * **Die Kennungen der Regeln des Regelregisters** — Auftrag `R-regelregister`
 * (2026-08-29), Fundament der Regel-Engine-Ära.
 *
 * **Eine Kennung ist nicht dasselbe wie eine Herkunft.** `herkunft` sagt, WIE
 * eine Zuordnung entstanden ist (von Hand, aus einem Vorschlag, maschinell);
 * `regel` sagt, WELCHE maschinelle Regel sie geschrieben hat. Solange es eine
 * einzige Regel gibt, fallen die beiden Auskünfte zusammen — mit der zweiten
 * Regel tun sie es nicht mehr, und eine Nachschau, die nur die Herkunft
 * zeigte, sagte dann „eine Maschine war es" und nicht mehr, welche.
 *
 * **Sie steht im Vertrag und nicht in `apps/api`**, weil sie über die
 * Schnittstelle geht: als Filter (`?regel=`) und als Feld je Zeile der
 * Nachschau. Das Register in `apps/api/src/regeln/register.ts` LIEST diesen
 * Typ und schreibt ihn nicht ein zweites Mal — dieselbe Arbeitsteilung wie bei
 * `kontierungsvorschlagQuelleSchema`, und aus demselben Grund
 * (`scripts/verify-api-enums.mjs`).
 *
 * `aufladung` — Regel 1, die erkannte Guthabenaufladung (`R-aufladungsauto`,
 * 2026-08-28). Weitere Kennungen kommen je Regel-Auftrag hinzu; jede neue
 * verlangt einen Eintrag im Register und dort die Angabe, was mit dem
 * Altbestand ihres Sachverhalts geschieht.
 *
 * `zd_auszahlung`, `zd_gebuehr`, `pp_lastschrift` — die Regeln 2 bis 4
 * (`R-regeln234`, 2026-08-29, Entscheidung `REI-4 = A`: **Umbuchungen
 * zuerst**). Alle drei fassen ausschliesslich Vorgänge an, die **keinen
 * Erfolg berühren oder ihn eindeutig benennen** und deshalb ohne die
 * Steuerlogik-Ära entschieden werden können:
 *
 * - `zd_auszahlung` — die Auszahlung eines Zahldienstes auf das Bankkonto,
 *   gebucht auf der BANKSEITE als `1200 an 1210`. Der Nordstern nennt sie
 *   ausdrücklich: „Die Auszahlung eines Zahlungsanbieters ist eine
 *   Umbuchung, kein Umsatz."
 * - `zd_gebuehr` — eine EIGENSTÄNDIGE Anbietergebühr (eine Bewegung, die
 *   nichts als eine Gebühr ist), gebucht als `4970 an 1210`. Nicht zu
 *   verwechseln mit der Gebühr, die AN einer Zahlung hängt
 *   (`bank_transaction.fee_amount`, Migration `0052`) — die bucht der
 *   allgemeine Pfad längst mit.
 * - `pp_lastschrift` — die Lastschrift, mit der PayPal das eigene
 *   Zahldienstkonto vom Bankkonto auffüllt, gebucht auf der BANKSEITE als
 *   `1210 an 1200`. Sie ist die Bewegung, für die `E-491` seit
 *   `C-kontbuchung` verbietet, sie als Aufwand zu buchen.
 *
 * `az_rueckbuchung` — Regel 5, die **Rückbuchung** einer
 * Abbuchungsvereinbarung des Shops (`R-azrueckbuchung`, 2026-09-03). Sie ist
 * die Gegenrichtung der Aufladung, die Regel 1 bucht: `1796` Soll (brutto) an
 * `1210` Haben (netto) und `4970` Haben (der erstattete Teil der Gebühr).
 * **Eine eigene Kennung und kein zweiter Zweig von `aufladung`** — Grundlage
 * ist Entscheidung `D-15` der Spec der Guthaben-Korrekturen: Eine Rückbuchung
 * ist keine Korrektur, sondern eine eigene Geldbewegung mit eigenem Beleg.
 */
export const regelkennungSchema = z.enum([
    'aufladung',
    'zd_auszahlung',
    'zd_gebuehr',
    'pp_lastschrift',
    'az_rueckbuchung',
]);
/**
 * Eine Position beim Schreiben — Betrag, Sachkonto, Steuerschlüssel, optionale
 * Begründung (Kanzleiantwort Punkt 4 und 5).
 *
 * **`accountNumber` ist eine Nummer und kein Fremdschlüssel** (Spec 3.3, wie
 * `vendor.default_account_number`). Die Gültigkeit zum Buchungsdatum prüft der
 * Dienst über `account-resolver.ts` (`E-378`) — hier steht nur die Form.
 *
 * **`amount` ist vorzeichenbehaftet wie `bank_transaction.amount`**, nicht auf
 * positiv beschränkt: Eine einzelne Position kann ausgleichend sein, solange
 * die Summe aller Positionen den Bewegungsbetrag trifft (`E-382`).
 */
export const kontierungPositionInputSchema = z
    .object({
    accountNumber: z.string().trim().regex(/^[0-9]{4,6}$/, 'Eine Kontonummer hat vier bis sechs Ziffern'),
    taxKey: z.string().trim().min(1).max(20).nullish(),
    amount: centAmount,
    begruendung: z.string().trim().min(1).max(2000).nullish(),
})
    .strict();
/** Die Angaben, die eine neue Kontierung — Anlage oder Korrektur — gemeinsam tragen. */
const kontierungKopfInputFelder = {
    customerId: uuid.nullish(),
    vendorId: uuid.nullish(),
    matchedDocumentId: uuid.nullish(),
    matchedIncomingInvoiceId: uuid.nullish(),
    vorschlagsquelle: z.string().trim().min(1).max(200).nullish(),
    begruendung: z.string().trim().min(1).max(2000).nullish(),
    positionen: z.array(kontierungPositionInputSchema).min(1, 'Mindestens eine Position'),
};
/**
 * `POST /api/v1/kontierungen` — die Erstanlage.
 *
 * **`herkunft` steht hier und nur hier im Rumpf.** Bei einer Korrektur setzt
 * der Dienst sie zwingend auf `korrektur` (Falle einer widersprüchlichen
 * Anfrage: „korrigiere, aber die Herkunft heisst von_hand").
 */
export const kontierungCreateRequestSchema = z
    .object({
    bankTransactionId: uuid,
    herkunft: kontierungHerkunftSchema,
    ...kontierungKopfInputFelder,
})
    .strict();
/**
 * `POST /api/v1/kontierungen/:id/korrektur` — die Nachfolgerin.
 *
 * **Kein `bankTransactionId` und kein `herkunft` im Rumpf.** Beide stehen
 * durch den Vorgänger bereits fest: Die Bewegung ändert sich durch eine
 * Korrektur nicht, und die Herkunft ist immer `korrektur` (`D-K2`).
 *
 * **`begruendung` ist hier Pflicht**, anders als bei der Erstanlage — Spec
 * 3.3 wörtlich: „Pflicht bei `von_hand` und `korrektur`, sonst leer." Eine
 * Korrektur OHNE Begründung wäre eine Änderung, deren Grund niemand
 * nachvollziehen kann — genau das, was GoBD Rz. 111 verlangt.
 */
export const kontierungKorrekturRequestSchema = z
    .object({
    ...kontierungKopfInputFelder,
    begruendung: z.string().trim().min(1).max(2000),
})
    .strict();
/**
 * `POST /api/v1/kontierungen/:id/aufheben` — Auftrag `W-kontkorrektur`
 * (Stufe A): eine noch nicht gebuchte Kontierung wird OHNE Ersatz gelöst. Die
 * Nachfolgerin trägt `herkunft = 'aufgehoben'` und keine Position (Migration
 * `0082`) — sie wartet wie jede andere Nachfolgerin auf die Freigabe (Vier
 * Augen, `A-K1`), bevor die Bewegung als "keine gültige Kontierung" gilt.
 *
 * **`begruendung` ist hier IMMER Pflicht**, anders als bei der Erstanlage:
 * Es gibt keinen Beleg, der die Pflicht ersetzen könnte (`kontierungKopfInputFelder`
 * kennt hier keine Positionen, an denen ein Beleg hängen könnte).
 */
export const kontierungAufhebungRequestSchema = z
    .object({
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000),
})
    .strict();
/**
 * `POST /api/v1/kontierungen/:id/storno` — Auftrag `W-kontkorrektur` (Stufe
 * B): eine BEREITS GEBUCHTE Kontierung wird über eine Stornobuchung im
 * Journal zurückgenommen (`JournalService.reverseWithin`, Generalumkehr).
 *
 * **Kein `positionen`-Feld** — die Gegenbuchung übernimmt die Zeilen der
 * Originalbuchung vertauscht (`JournalService`s Kopfkommentar zu `reverse()`);
 * eine zweite Eingabe der Positionen wäre eine zweite, abweichbare Quelle
 * derselben Zahlen.
 */
export const kontierungStornoRequestSchema = z
    .object({
    begruendung: z.string().trim().min(1, 'Begründung darf nicht leer sein').max(2000),
})
    .strict();
/**
 * Eine Position beim Lesen.
 *
 * **Kunde und Beleg je Position, nur lesend** (seit 2026-09-30, `ZZ-mkweb`,
 * Mehrkunden-Spec `docs/superpowers/specs/2026-09-26-mehrkunden-sammeleinzug-design.md`
 * `D-2`, `D-20`, `D-25`): `kontierung_position.customer_id` und `document_id`
 * (Migration `0244`), dazu ihre Anzeigeformen `kundennummer`
 * (`customer.display_customer_no`) und `documentNumber` (`document.number`).
 * Gesetzt werden die Spalten ausschliesslich vom Buchungsweg der
 * Zahlungszuordnung und von der Freigabe, die sie mitkopiert (`D-5`); jede
 * andere Kontierung trägt hier viermal `null`. Die Eingabeform
 * {@link kontierungPositionInputSchema} nimmt sie ausdrücklich nicht an.
 */
export const kontierungPositionSchema = z
    .object({
    id: uuid,
    position: z.number().int().positive(),
    accountNumber: z.string(),
    taxKey: z.string().nullable(),
    amount: centAmount,
    begruendung: z.string().nullable(),
    customerId: uuid.nullable(),
    kundennummer: z.string().nullable(),
    documentId: uuid.nullable(),
    documentNumber: z.string().nullable(),
})
    .strict();
/** Der Kopf beim Lesen, samt seiner Positionen. */
export const kontierungSchema = z
    .object({
    id: uuid,
    bankTransactionId: uuid,
    customerId: uuid.nullable(),
    vendorId: uuid.nullable(),
    matchedDocumentId: uuid.nullable(),
    matchedIncomingInvoiceId: uuid.nullable(),
    herkunft: kontierungHerkunftSchema,
    vorschlagsquelle: z.string().nullable(),
    begruendung: z.string().nullable(),
    ersetztKontierungId: uuid.nullable(),
    freigegebenVon: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
    /**
     * Einzelfreigabe (`D-31`, `0263`): an einer Freigabezeile, wenn Urheber und
     * Freigebender dieselbe Person sind; an einer Stornozeile, wenn die Person
     * storniert, die freigegeben hatte. Nur unter einer Freigaberegel ohne vier
     * Augen. `optional()`, weil ältere Fixturen das Feld nicht führen.
     */
    einzelfreigabe: z.boolean().optional(),
    createdBy: uuid,
    createdAt: z.string(),
    positionen: z.array(kontierungPositionSchema),
})
    .strict();
/**
 * Die Anzeigeform einer Bewegung für `GET /kontierungen/bewegung/:id` —
 * bewusst schmal, nur was diese eine Maske zeigt (Datum, Betrag,
 * Verwendungszweck, Gegenpartei), nicht `bankTransactionSchema` mit all
 * seinen Feldern ohne Leser an dieser Adresse.
 *
 * **Angelegt bei `O-vertraege2` (2026-08-29), aus zwei Abschriften
 * zusammengeführt.** `apps/api/src/kontierung/kontierung.service.ts`
 * (`BewegungsstandBewegung`) und `apps/web/src/api/schemas.ts`
 * (`bewegungsstandBewegungSchema`) führten dieselben acht Felder unter
 * demselben Namen bereits vorher — der Dienst als `string`-Interface, der
 * Web-Spiegel als eigenes `z.object(...)`. Beide waren, Feld für Feld,
 * bereits deckungsgleich; keine Differenz zwischen den zwei Abschriften war
 * zu klären.
 */
export const bewegungsstandBewegungSchema = z
    .object({
    id: uuid,
    bankAccountId: uuid,
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    /**
     * `bank_transaction.counterparty_iban` (Auftrag `DR-bewegungiban`) — die
     * IBAN der Gegenseite, `null` wo es keine gibt (PayPal, Mollie). Der
     * Regel-Builder („Regel aus Bewegung“) kreuzt sie als Kriterium an: eine
     * Regel auf die IBAN überlebt die Umbenennung des Zahlers, eine auf den
     * Namen nicht. So gespeichert, wie `bank_transaction` sie führt, durchgereicht,
     * nicht neu normalisiert.
     */
    counterpartyIban: z.string().nullable(),
    /**
     * `bank_transaction.journal_entry_id` — gesetzt heisst, die Bewegung ist
     * bereits gebucht (Stufe B); die Maske bietet den Storno-Weg nur dann an.
     */
    journalEntryId: uuid.nullable(),
})
    .strict();
/**
 * `GET /api/v1/kontierungen/bewegung/:bankTransactionId` — Bewegung samt
 * ihrer aktuell gültigen Kontierung (oder `null`) und der Korrekturkette
 * ihrer Vorgänger, jüngster zuerst (Auftrag `W-kontkorrektur`).
 */
export const bewegungsstandAntwortSchema = z
    .object({
    bewegung: bewegungsstandBewegungSchema,
    kontierung: kontierungSchema.nullable(),
    historie: z.array(kontierungSchema),
})
    .strict();
/** `GET /api/v1/bank-accounts/:id/unkontiert` — die Liste. */
export const unkontierteListeSchema = z
    .object({
    transactions: z.array(bankTransactionSchema),
    total: z.number().int().nonnegative(),
    gesamtOhneFilter: z.number().int().nonnegative(),
})
    .strict();
/**
 * Warum eine Bewegung (noch) nicht vollständig kontiert und gebucht ist —
 * Spec 5.4; dieselbe Bauart wie `nichtbuchungsgrundSchema` auf der
 * Eingangsseite.
 *
 * **`stufe_c_fehlt` heisst seit `C-kontbuchung`: freigegeben, aber nicht
 * gebucht.** Bis dahin stand er auf JEDER Zeile, weil es keinen Buchungspfad
 * gab und deshalb keine Bewegung abgeschlossen war (`E-395` in seiner ersten
 * Fassung). Seit der Buchungspfad steht, wäre das die umgekehrte Lüge: Eine
 * gebuchte Bewegung als „Stufe C fehlt" zu melden, machte den Bericht binnen
 * eines Monats unlesbar. Eine gebuchte Bewegung trägt deshalb keinen Grund
 * mehr — und erscheint gar nicht erst in `bewegungen` (`E-493`).
 *
 * **Die drei zusätzlichen Gründe seit `C-kontbuchung`** sind die
 * Buchungshindernisse aus `apps/api/src/kontierung/buchung.ts` — eine
 * freigegebene, gültig kontierte Bewegung, die trotzdem nicht gebucht wurde,
 * sagt hier WARUM statt still `stufe_c_fehlt` zu tragen:
 *
 * - `begruendung_fehlt` — `D-K6`: Die Kontierung zeigt auf ein Erfolgskonto,
 *   weder eine Ausgangs- noch eine Eingangsrechnung ist zugeordnet, UND es
 *   fehlt eine nichtleere Begründung auf dem Kopf ODER auf mindestens einer
 *   Position. **Bis `C-dk6oeffnen` (2026-08-28) hiess dieser Grund
 *   `beleg_fehlt` und blockierte die Buchung ganz** — `KS-2` ist seither
 *   selbst geklärt (`docs/scribbles/2026-08-28-selbstklaerung-steuerfragen.md`
 *   Punkt 1): GoBD Rz. 62 zählt „betriebliche Kontoauszüge" als Belegart, der
 *   Kontoauszug selbst ist also immer schon der Buchungsbeleg. Was ohne
 *   Fremdbeleg (Rz. 61) fehlen kann, ist nur noch die dokumentierte
 *   Begründung — kein Verbot mehr, sondern eine Pflicht.
 * - `gebuehr_nicht_verteilbar` — Die Bewegung trägt eine Anbietergebühr
 *   (`fee_amount`) und MEHR als eine Position; wie die Gebühr auf zwei
 *   Sachkonten aufzuteilen wäre, entscheidet keine Spec, und eine erfundene
 *   Quote wäre dieselbe Sorte Erfindung, die `A-K7` verbietet.
 * - `konto_nicht_benannt` — bestand bereits und trägt jetzt zwei Lesarten
 *   derselben Sache: eine Position ohne zum Buchungsdatum gültige
 *   Kontenversion.
 */
export const nichtkontiertGrundSchema = z.enum([
    'keine_zuordnung',
    'konto_nicht_benannt',
    'vor_zustaendigkeitsbeginn',
    'freigabe_fehlt',
    'stufe_c_fehlt',
    'begruendung_fehlt',
    'gebuehr_nicht_verteilbar',
]);
export const nichtkontierteBewegungSchema = z
    .object({
    bankTransactionId: uuid,
    bankAccountId: uuid,
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    customerId: uuid.nullable(),
    vendorId: uuid.nullable(),
    gruende: z.array(nichtkontiertGrundSchema),
})
    .strict();
/** `GET /api/v1/berichte/nichtkontiert` — nie stillschweigend leer (`E-395`, `E-493`). */
export const nichtkontiertBerichtSchema = z
    .object({
    anzahl: z.number().int().nonnegative(),
    bewegungen: z.array(nichtkontierteBewegungSchema),
    jeGrund: z.record(nichtkontiertGrundSchema, z.number().int().nonnegative()),
})
    .strict();
/**
 * `GET /api/v1/berichte/teilzahlungen` — Aufgabe 10 `[V-klaerliste4]`, Spec 4.3
 * und 4.5: der eigene Klärlisten-Abschnitt für eine erkannte, aber NICHT
 * verteilte Teilzahlung (`A-K7`, `E-402`).
 *
 * **Live berechnet, nicht gespeichert.** `apps/api/src/kontierung/abgleich.ts`s
 * `sucheEingangsrechnung()` trägt das `teilzahlung`-Flag seit `C-abgleich3` —
 * dieser Vertrag beschreibt nur, was der Bericht daraus für die Klärliste
 * zusammenstellt: die Bewegung, der gefundene Kreditor samt Namen und die
 * gefundene Rechnung samt Nummer und Bruttobetrag, damit ein Mensch den Fall
 * ohne einen zweiten Abruf beurteilen kann.
 */
export const teilzahlungBewegungSchema = z
    .object({
    bankTransactionId: uuid,
    bankAccountId: uuid,
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    vendorId: uuid,
    vendorName: z.string(),
    incomingInvoiceId: uuid,
    invoiceNo: z.string().nullable(),
    grossTotal: centAmount,
})
    .strict();
export const teilzahlungListeSchema = z
    .object({ bewegungen: z.array(teilzahlungBewegungSchema) })
    .strict();
/**
 * `GET /api/v1/kontierungen/wartend` — Auftrag `W-freigabeliste` (2026-08-27):
 * der kanalübergreifende Klärlisten-Abschnitt „Wartet auf Freigabe", der
 * Handbuch §3.281 offen liess (`bewegungsstand()` und die Direktadresse lösen
 * dort nur das FINDEN einer bekannten Bewegung, nicht das ENTDECKEN aller
 * wartenden).
 *
 * **Dieselbe Kandidatenmenge wie `kontierung_status = 'in_arbeit'` in
 * `bank.query.ts`** — der Kopf der Korrekturkette (`NOT EXISTS
 * ersetzt_kontierung_id`), noch nicht freigegeben. Diese Liste stellt genau
 * diese Bedingung KONTENÜBERGREIFEND, ohne den Umweg über ein einzelnes Konto.
 *
 * **`positionen` trägt die vollen Zielkonten samt Beträgen — dieselbe Form
 * wie `kontierungSchema.positionen`.** Die Summe ergibt immer den
 * Bewegungsbetrag (`E-382`, aufgeschoben bis zum COMMIT); eine eigene
 * „Positionssumme" wäre eine zweite Wahrheit für einen Wert, der durch die
 * Datenbank bereits erzwungen ist. Eine zweite Person prüft an dieser Liste
 * die ZIELKONTEN, nicht die Additionsarbeit der Datenbank.
 *
 * **`alterTage` kommt server-seitig** — dieselbe Bauart wie
 * `tageUeberfaellig` in `gaps.report.ts`: ein Tageszähler wird einmal in SQL
 * gebildet, nicht im Browser aus `createdAt` nachgerechnet und damit von der
 * Zeitzone des Betrachters abhängig.
 *
 * **`createdBy` bleibt eine reine Kennung**, wie überall sonst in diesem
 * System (`journalEntrySchema.createdBy`, `documentSchema.createdBy`) — keine
 * neue Namensauflösung nur für diese Liste. Die Oberfläche vergleicht sie
 * gegen die eigene `userId` für die „von Ihnen"-Markierung.
 */
export const kontierungWartendZeileSchema = z
    .object({
    kontierungId: uuid,
    bankTransactionId: uuid,
    bankAccountId: uuid,
    bankAccountName: z.string(),
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    positionen: z.array(kontierungPositionSchema),
    createdBy: uuid,
    createdAt: z.string(),
    alterTage: z.number().int().nonnegative(),
})
    .strict();
export const kontierungWartendListeSchema = z
    .object({ bewegungen: z.array(kontierungWartendZeileSchema) })
    .strict();
/**
 * `GET /api/v1/kontierungen/automatisch` — die **Nachschau** über die
 * automatisch gebuchten Kontierungen. Auftrag `R-aufladungsauto`
 * (2026-08-28), Migration `0092`.
 *
 * **Warum es diese Liste geben MUSS und nicht bloss geben kann.** Die
 * automatische Regel bucht ohne die zweite Person, die jede andere Kontierung
 * braucht (`kontierung_vier_augen`, `E-380`). Was an deren Stelle tritt, ist
 * die Regel selbst, die Aktivierungsgrenze — und diese Liste: Eine
 * Vier-Augen-Ersetzung ohne Nachschau wäre eine Buchung, die niemand je zu
 * Gesicht bekommt. Der Auftraggeber hat sie deshalb ausdrücklich mitbestellt.
 *
 * **Sie zeigt das ERGEBNIS, nicht einen Vorgang.** Anders als „Wartet auf
 * Freigabe" ist hier nichts zu tun: Jede Zeile ist fertig gebucht und trägt
 * ihre `journalEntryId`. Wer eine davon für falsch hält, geht den Storno-Weg
 * (`POST /api/v1/kontierungen/:id/storno`) — und der verlangt seinerseits eine
 * zweite Person.
 *
 * **Dieselbe Form wie `kontierungWartendZeileSchema`, plus zwei Felder und
 * minus eines.** Plus `journalEntryId` (die Buchung, die entstanden ist) und
 * `freigegebenAm` (wann die Regel gelaufen ist); minus `createdBy`/`alterTage`
 * — der Urheber ist immer der Systembenutzer, und ein Alter zählt nur für
 * etwas, das noch wartet.
 */
export const kontierungAutomatischZeileSchema = z
    .object({
    kontierungId: uuid,
    bankTransactionId: uuid,
    bankAccountId: uuid,
    bankAccountName: z.string(),
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    positionen: z.array(kontierungPositionSchema),
    /**
     * **Welche Regel des Registers diese Zeile geschrieben hat.**
     *
     * Nachtrag `R-regelregister` (2026-08-29). Solange es eine einzige Regel
     * gibt, ist der Wert immer `aufladung` und wirkt überflüssig; genau
     * deshalb steht er JETZT hier und nicht erst mit der zweiten Regel. Eine
     * Nachschau, die mehrere Regeln in einer Liste zeigt, ohne je Zeile zu
     * sagen, welche gebucht hat, verlangt vom Leser, die Regel aus dem
     * Verwendungszweck zu erraten — und das ist genau die Arbeit, die diese
     * Liste ihm abnehmen soll.
     */
    /**
     * **`null` heisst: eine DATENREGEL war es** — Nachtrag Auftrag
     * `R-drautomatik` (2026-09-01), Migration `0113`.
     *
     * Eine Datenregel ist kein Eintrag im Regelregister und hat deshalb keine
     * {@link Regelkennung}; ihre Urheberin steht in {@link regelVersionId} und
     * {@link regelName} daneben. Eine erfundene Kennung wäre die bequemere
     * Fassung und zugleich die falsche: Sie behauptete einen Eintrag im
     * Quelltext, den es nicht gibt.
     */
    regel: regelkennungSchema.nullable(),
    /**
     * **Die Fassung der Datenregel, die diese Zeile geschrieben hat** — `null`
     * bei jeder Code-Regel. Genau dann gesetzt, wenn `regel` `null` ist; die
     * Datenbank hält dieselbe Äquivalenz gegen die Herkunftsmarke (`E-593`).
     */
    regelVersionId: uuid.nullable(),
    /**
     * **Der Name der Datenregel** — `null` bei jeder Code-Regel.
     *
     * Er steht neben der Fassungskennung und nicht statt ihrer: Der Name ist
     * für einen Menschen lesbar, die Kennung ist eindeutig. Beides zusammen
     * ist genau die Auskunft, die auch der Begründungstext auf der Kontierung
     * gibt (`E-594`).
     */
    regelName: z.string().nullable(),
    /** Die Buchung, die die Regel erzeugt hat. `null` heisst: kontiert, aber nicht gebucht. */
    journalEntryId: uuid.nullable(),
    freigegebenAm: z.string().nullable(),
})
    .strict();
/**
 * **`gesamt` ist die Zahl über den GANZEN Bestand, `bewegungen` das gekappte
 * Fenster darauf** — dieselbe Trennung wie `total`/`gesamtOhneFilter` in
 * `unkontierteListeSchema`. Eine Nachschau, die bei 200 Zeilen abschneidet und
 * die Gesamtzahl verschweigt, sähe nach einem ruhigen Monat aus, auch wenn die
 * Regel tausendmal gelaufen ist.
 */
export const kontierungAutomatischListeSchema = z
    .object({
    bewegungen: z.array(kontierungAutomatischZeileSchema),
    gesamt: z.number().int().nonnegative(),
})
    .strict();
/**
 * `GET /api/v1/kontierungen/vorschlag/:bankTransactionId` — die Verträge des
 * Kontierungsvorschlags (Task 7 `C-kontvorschlag`, Spec 3.4). Der Endpunkt
 * selbst ist ein Nachtrag von Aufgabe 9 `[V-kontmaske]`: `C-kontapi` hat ihn
 * nicht ausgeliefert (Plan-Nachtrag 2026-08-26, Punkt 5) — die Maske braucht
 * ihn zur Vorbelegung.
 *
 * **Die Form spiegelt `apps/api/src/kontierung/vorschlag.ts::Kontierungsvorschlag`
 * wortgleich.** Kein `amount` je Position — dieselbe Vereinfachung wie dort
 * (Kopfkommentar der Datei): Es gibt in dieser Ära höchstens eine
 * vorgeschlagene Position, und sie deckt immer den vollen Bewegungsbetrag;
 * die Maske füllt ihn beim Übernehmen aus der Bewegung selbst.
 *
 * **Der vierte Wert `datenregel` steht an DRITTER Stelle** (`D-12`, Auftrag
 * `R-drvorschlag`, 2026-09-01). Die Aufzählung ist zwar nur eine Liste — die
 * Zusicherung über die Reihenfolge trägt `ermittleKontierungsvorschlag()` und
 * nicht dieser Vertrag (`E-589`) —, aber sie steht hier in derselben Folge,
 * weil zwei verschiedene Reihenfolgen für dieselbe Sache die Stelle wären, an
 * der jemand die falsche liest.
 *
 * **Der fünfte Wert `katalog_stichwort` steht an LETZTER Stelle** (Auftrag
 * `K-erkennungneu`, 2026-09-10). Ein Treffer über `kontenkatalog.stichworte`
 * ist ein Wort im Verwendungszweck, keine erfasste Kreditor-Zuordnung und
 * keine formulierte Regel — er ist die schwächste der fünf Quellen und tritt
 * nie vor einer gelernten Kreditor-Zuordnung (`gegenpartei_iban`,
 * `eigene_vergangenheit`) oder einer Datenregel an (`E-1248`). Auch hier trägt
 * `ermittleKontierungsvorschlag()` die Zusicherung, nicht dieser Vertrag.
 *
 * **Ein neuer Aufzählungswert in einer ANTWORT ist additiv.** `bruch.ts`
 * meldet nur den ENTFALLENEN Wert; ein hinzugekommener bricht keinen Leser,
 * der die alten kennt. Umgekehrt wäre es ein Bruch, stünde dieselbe
 * Aufzählung in einer ANFRAGE — dort tut sie das nicht.
 */
export const kontierungsvorschlagQuelleSchema = z.enum([
    'erkannter_schluessel',
    'gegenpartei_iban',
    'datenregel',
    'eigene_vergangenheit',
    'katalog_stichwort',
]);
export const kontierungsvorschlagPositionSchema = z
    .object({
    accountNumber: z.string(),
    taxKey: z.string().nullable(),
})
    .strict();
export const kontierungsvorschlagSchema = z
    .object({
    quelle: kontierungsvorschlagQuelleSchema,
    customerId: uuid.nullable(),
    vendorId: uuid.nullable(),
    matchedDocumentId: uuid.nullable(),
    matchedIncomingInvoiceId: uuid.nullable(),
    positionen: z.array(kontierungsvorschlagPositionSchema).min(1),
    herkunftBeschreibung: z.string(),
    /**
     * Die Fassung der Datenregel, die den Vorschlag getragen hat — **nur bei
     * `quelle === 'datenregel'` gesetzt** (`D-12`, Auftrag `R-drvorschlag`).
     *
     * **Optional und nicht `nullable`**, und das ist eine Vertragsentscheidung
     * und keine Bequemlichkeit: Ein neues PFLICHTfeld in einer Antwort ist für
     * `pnpm api:spec:check` ein Bruch (`bruch.ts`, „Feld … ist neu Pflicht"),
     * ein optionales nicht. Die drei älteren Quellen lassen es deshalb weg,
     * statt `null` zu schicken — und ein Leser, der es nicht kennt, sieht
     * dieselbe Antwort wie vorher.
     *
     * **Es steht hier und nicht auf der Position.** Der Vorschlag hat GENAU
     * EINE Position (Kopfkommentar oben), und die Fassung gilt für die
     * Zuordnung als Ganzes — Konto, Steuerschlüssel und Gegenpartei kommen aus
     * derselben Zeile `regel_version`.
     */
    regelVersionId: uuid.optional(),
})
    .strict();
/** `null`, wenn keine der fünf Quellen etwas liefert — keine Bewegung ohne Antwort. */
export const kontierungsvorschlagAntwortSchema = z
    .object({ vorschlag: kontierungsvorschlagSchema.nullable() })
    .strict();
// --- Der Rundweg von der Eingangsrechnung zur Bewegung — Aufgabe `[W-manuellbind]` ----
//
// Der Auftraggeber (2026-08-27): „Beim Erfassen einer Eingangsrechnung kann
// man keinen Bankvorgang auswählen — es sollte auch andersherum gehen, am
// besten mit einem automatischen Zuweisungsversuch." Die drei Formen hier
// tragen genau das: einen offenen Bankvorgang zur manuellen Wahl
// (`bewegungskandidatSchema`), den automatischen Fund „Rechnung → Bewegung"
// — die Umkehrung von Richtung 2 aus `abgleich.ts` (IBAN, dann normalisierte
// Kreditor-Rechnungsnummer) — als Lesedienst für die Erfassungsmaske, und die
// Suchantwort für die manuelle Wahl. Die eigentliche VERKNÜPFUNG entsteht
// weiterhin über `POST /api/v1/kontierungen` (`kontierungCreateRequestSchema`
// oben) — kein eigener Schreibweg, siehe Kopfkommentar von
// `apps/api/src/kontierung/abgleich.ts` zur Richtung 2 und
// `apps/api/src/kontierung/rechnung-bewegung.ts`.
/**
 * Ein offener Bankvorgang — „offen" heisst: keine gültige, nicht ersetzte
 * Kontierung hängt bereits an ihm (`kontierung.ersetzt_kontierung_id IS
 * NULL`). Dieselbe schmale Form wie `bewegungsstandBewegungSchema` im
 * Web-Spiegel, hier als geteilter Vertrag für zwei Endpunkte.
 */
export const bewegungskandidatSchema = z
    .object({
    bankTransactionId: uuid,
    bankAccountId: uuid,
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
})
    .strict();
/**
 * **Auf welcher Stufe der Leiter der Fund entstanden ist** — Auftrag
 * `ER-bewegungsleiter` (2026-09-16), Nachtrag zu `D-K5`.
 *
 * Die Reihenfolge ist zugleich die Rangfolge, und die erste treffende Stufe
 * gewinnt. Sie sagt, WIE gut der Fund belegt ist, und wird deshalb bis in die
 * Beschriftung der Erfassungsmaske durchgereicht: Ein Fund über IBAN und
 * Rechnungsnummer ist etwas anderes als einer über Name und Betrag, und der
 * Erfasser, der ihn bestätigt, soll den Unterschied sehen.
 *
 * | Stufe | Merkmale |
 * |---|---|
 * | `iban_nummer` | Gegenpartei-IBAN **und** normalisierte Rechnungsnummer im Verwendungszweck |
 * | `iban_betrag` | Gegenpartei-IBAN **und** gleicher Betrag im Datumsfenster |
 * | `nummer_betrag` | Rechnungsnummer im Verwendungszweck **und** gleicher Betrag |
 * | `name_betrag` | Gegenpartei-Name ≈ Kreditorname **und** gleicher Betrag im Datumsfenster |
 */
export const bewegungsfundSicherheitSchema = z.enum([
    'iban_nummer',
    'iban_betrag',
    'nummer_betrag',
    'name_betrag',
]);
/**
 * **Warum kein Fund entstanden ist** — Auftrag `ER-bewegungsleiter`
 * (2026-09-16).
 *
 * `mehrdeutig` ist der einzige der drei Gründe, der ETWAS gefunden hat: Auf
 * einer Stufe passten mehrere offene Bewegungen, und die Leiter hört dort auf,
 * statt auf eine schwächere Stufe auszuweichen — Mehrdeutigkeit ist ein Befund
 * und kein Anlass zum Lockern. `kandidaten` in der Antwort nennt dann, wie
 * viele es waren. `kein_kandidat`: keine Stufe hat etwas gefunden.
 * `kein_kreditor`: der Beleg trägt keinen aktiven Kreditor, es gibt gar nichts
 * zu suchen.
 */
export const bewegungsfundGrundSchema = z.enum([
    'mehrdeutig',
    'kein_kandidat',
    'kein_kreditor',
]);
/**
 * Der automatische Fund „Rechnung → Bewegung" — die Umkehrung von
 * `sucheEingangsrechnung()` in `abgleich.ts`: Diese Richtung startet bei der
 * Rechnung und sucht die Bewegung, nicht umgekehrt.
 *
 * **Seit `ER-bewegungsleiter` (2026-09-16) nennt der Treffer seine Stufe.**
 * Vorher trug er sie nicht, weil es nur eine gab.
 */
export const rechnungBewegungTrefferSchema = z
    .object({
    bankTransactionId: uuid,
    bankAccountId: uuid,
    bookingDate: isoDate,
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    counterpartyName: z.string().nullable(),
    /** `E-402`-Bauart: erkannt, nicht verteilt — keine Aufteilung einer Teilzahlung. */
    teilzahlung: z.boolean(),
    sicherheit: bewegungsfundSicherheitSchema,
})
    .strict();
/**
 * Steht eine Eingangsrechnung bereits mit einer Bewegung in Verbindung — über
 * die aktuell gültige Kontierung (`ersetzt_kontierung_id IS NULL`), gelesen
 * von der Rechnung aus. Steht das, bietet die Erfassungsmaske weder den
 * automatischen Fund noch die manuelle Suche mehr an — sie zeigt den Stand.
 */
export const bestehendeVerknuepfungSchema = z
    .object({
    kontierungId: uuid,
    bankTransactionId: uuid,
    freigegebenAm: z.string().nullable(),
})
    .strict();
/**
 * `GET /api/v1/incoming-invoices/:id/bewegungsvorschlag`.
 *
 * `treffer` ist `null`, wenn nichts Eindeutiges gefunden wurde ODER wenn
 * `bereitsVerknuepft` gesetzt ist (dann wird gar nicht erst gesucht — siehe
 * `IncomingInvoiceController.bewegungsvorschlag()`).
 */
export const rechnungBewegungAntwortSchema = z
    .object({
    treffer: rechnungBewegungTrefferSchema.nullable(),
    bereitsVerknuepft: bestehendeVerknuepfungSchema.nullable(),
    /**
     * Steht NUR neben `treffer: null` aus einer wirklich gefahrenen Suche —
     * nicht neben `bereitsVerknuepft`, denn dort wurde gar nicht erst gesucht.
     */
    grund: bewegungsfundGrundSchema.optional(),
    /** Die Zahl der Kandidaten der Stufe, die mehrdeutig war — nur bei `grund: 'mehrdeutig'`. */
    kandidaten: z.number().int().nonnegative().optional(),
})
    .strict();
/** `GET /api/v1/incoming-invoices/bewegungssuche` — die manuelle Suche über offene Bewegungen. */
export const bewegungssucheAntwortSchema = z
    .object({ bewegungen: z.array(bewegungskandidatSchema) })
    .strict();
/**
 * Ein Anteil zwischen 0 und 1 einschliesslich — dieselbe Bauart wie
 * `taxRateFactor` (`documents.ts`), nur mit der GESCHLOSSENEN statt der
 * offenen oberen Grenze: Ein Steuersatz von 100 % ist ausgeschlossen, eine
 * Zuordnungsquote von 100 % (alles kontiert) ist der Normalfall, den ein
 * frisches Konto am ersten Tag schon erreichen kann.
 */
const quoteFactor = z.string().transform((wert, ctx) => {
    let quote;
    try {
        quote = factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Keine gültige Quote' });
        return z.NEVER;
    }
    // Verglichen wird DEZIMAL über `packages/money`, niemals über `Number` --
    // derselbe Grund wie bei `taxRateFactor`.
    const alsBetrag = money(quote);
    if (isNegative(alsBetrag) || cmp(alsBetrag, money('1')) > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Die Quote liegt ausserhalb von 0 bis 1' });
        return z.NEVER;
    }
    return quote;
});
/**
 * Die kontenübergreifende Zuordnungsquote — Anteil kontierter Geldbewegungen
 * über ALLE aktiven Geldkonten, EINE Zahl statt N Frontend-Abrufe.
 * Dashboard-Design (`docs/scribbles/2026-08-31-dashboard-design.md`
 * Abschnitt 2e, „Anteil kontierter/zugeordneter Zahlungen, kontenübergreifend
 * als EINE Quote"; Auftrag `B-dashquote`). **Deckt `E-569`.**
 *
 * **„Kontiert" heisst hier dasselbe wie in `unkontiert.query.ts`: irgendeine
 * Zeile in `kontierung`, unabhängig vom Freigabestand.** Eine Bewegung mit
 * einer angelegten, aber noch nicht freigegebenen Kontierung ist bereits in
 * Arbeit — sie als „unkontiert" zu zählen, zöge die Quote unter ihren
 * ehrlichen Wert.
 *
 * **`quote` ist `null`, wenn `bewegungenGesamt` null ist** — ein frisches
 * System ohne jede Geldbewegung zeigt „Noch keine Bankbewegungen" und keine
 * erfundene `100 %` oder `— %` (Design Abschnitt 7, Leere Zustände).
 *
 * **Nur AKTIVE Konten zählen mit.** Ein geschlossenes Konto ist kein
 * Arbeitsvorrat mehr; seine Alt-Bewegungen in die Quote zu mischen, senkte sie
 * ohne Aussicht auf Besserung.
 */
export const zuordnungsquoteBerichtSchema = z
    .object({
    /** `null` ohne jede Geldbewegung über alle aktiven Konten — keine erfundene Quote. */
    quote: quoteFactor.nullable(),
    bewegungenGesamt: z.number().int().nonnegative(),
    bewegungenKontiert: z.number().int().nonnegative(),
    /** Wie viele aktive Geldkonten in die Zählung eingeflossen sind. */
    kontenGezaehlt: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=kontierung.js.map